// "Update Code" — tell devices to pull their firmware .bin from the server and
// flash themselves.
//
// ⚠️ NOT THE SAME THING AS THE APP OTA. Two different systems, both called OTA:
//   • App OTA  (core/ota/*)  replaces the React bundle, served from anuplights.com
//   • THIS      firmware OTA  makes an ESP device fetch its .bin from
//                             smartifyindia.co.in/uploads/bin/ and reflash
// Nothing here touches the app bundle, and nothing there touches a device.
//
import { getRooms, getCommonDevices, getDevicesByRoom } from '../api/api';
import { publishCommand, publishRaw } from '../utils/deviceController';
import { getStatusCached, ONLINE, UNKNOWN } from './deviceStatus';
import { probeLiveness } from './liveness';

// ────────────────────────────────────────────────────────────────────────────
// The command the ESP8266 firmware listens for.
//
// The firmware accepts FOUR spellings, and upper-cases the word before matching:
//
//   tempword == "UPDATECODE" || "UPDATE CODE" || "CODEUPDATE" || "CODE UPDATE"
//
// so case and the space are both irrelevant here — "update code" and
// "updatecode" are equally valid. `updatecode` is used because it matches the
// working MQTT console's preset list, and lowercase is the house style for every
// simple word command.
//
// See core/devices/commands.js for the one real exception: ADDWIFI / REMOVEWIFI
// are NOT upper-cased by the firmware, because an SSID and password are
// case-sensitive — those are exact literals.
export const FIRMWARE_UPDATE_COMMAND = 'updatecode';

// How the command goes on the wire.
//   false -> `[<uno>]update code#<userId>`  — the normal device-command envelope,
//                                             what every relay/fan/light command
//                                             uses. The firmware substring-matches
//                                             the payload, so the command is found
//                                             inside the envelope.
//   true  -> `update code`                  — raw, the way `getallwifi` goes out
//                                             (Flutter's devController drops the
//                                             envelope when given a buttonId).
//
// Set to the envelope form, since that is what every functional device command
// uses; only queries like getallwifi are sent raw. The confirmation dialog prints
// the exact payload before publishing, so flip this if the firmware wants it bare.
export const FIRMWARE_UPDATE_UNWRAPPED = false;
// ────────────────────────────────────────────────────────────────────────────

// Liveness comes from THE SAME CHECK THAT DRAWS THE DOT — deviceStatus's
// getStatusCached, which asks GetDeviceStatusbyUniqueNumber.
//
// ⚠️ AN EARLIER VERSION HAD ITS OWN CHECK AND IT WAS WRONG TWICE OVER, so that a
// device showing a green dot was reported offline and refused an update:
//
//  1. It read LastUpdatedDateTime off the DEVICE LIST row, which is not the same
//     heartbeat the status table records.
//  2. It stripped the trailing 'Z' before parsing — treating a UTC timestamp as
//     local time. In IST that reads 5½ hours in the past, so the 60-second window
//     could never be met and EVERY device looked offline.
//
// Beyond the parsing, the rule is: whatever the user sees on the tile is what the
// action must act on. Two independent liveness checks will drift, and the user
// only ever sees one of them. getStatusCached is cached for 45 s and shared with
// the tiles, so on a screen whose dots have already rendered this is free.
export const LIVE_WINDOW_MS = 60000;

const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

// Status lookups in flight at once. A browser allows about six connections per
// host; fire fifty and the ones at the back sit in a queue until their own
// timeout fires, which reads as "offline" and gets worse the larger the scope.
export const STATUS_CONCURRENCY = 6;

// Promise.all with a ceiling. Results keep the order of `items`.
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
  return out;
}

function dedupe(devices) {
  const seen = new Set();
  return devices.filter((d) => {
    const u = String(unoOf(d) ?? '');
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}

// Every device in a room.
export async function collectRoomDevices(userId, roomId) {
  const res = await getDevicesByRoom(userId, roomId).catch(() => null);
  return dedupe(Array.isArray(res?.data) ? res.data : []);
}

// Every device in a location: each room's devices plus the location's common
// devices (which belong to no room and would otherwise be missed).
export async function collectLocationDevices(userId, locationId) {
  const [roomsRes, commonRes] = await Promise.all([
    getRooms(userId, locationId).catch(() => null),
    getCommonDevices(userId, locationId).catch(() => null),
  ]);
  const rooms = Array.isArray(roomsRes?.data) ? roomsRes.data : [];
  const perRoom = await Promise.all(
    rooms.map((r) => getDevicesByRoom(userId, r.RoomID)
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => [])),
  );
  const common = Array.isArray(commonRes?.data) ? commonRes.data : [];
  return dedupe([...perRoom.flat(), ...common]);
}

// Split a device list into the ones that will be sent to and the ones skipped.
//
// ⚠️ THIS ASKS THE DEVICES, NOT THE SERVER. Two earlier versions read a
// timestamp and both got it wrong in ways that reported working hardware as
// offline, because a timestamp answers "when did a row last change" and the
// question here is "will this device receive what I am about to send".
//
// What went wrong with the timestamp approach, in order:
//   1. it read the device-list row, which is not the heartbeat at all
//   2. it stripped the trailing 'Z', parsing UTC as local
//   3. even reading the right table, those stamps are the server's LOCAL time
//      labelled UTC — five and a half hours out (see deviceStatus.js)
//   4. and one HTTP request per device, each racing a 6-second timeout, means a
//      location's worth of devices queues behind the browser's six-connection
//      limit until the ones at the back time out and report offline. The bigger
//      the scope, the more of it falsely reads as dead.
//
// An MQTT probe has none of that: one connection, no clock, and it exercises the
// exact path the command will take. Verified against live hardware.
//
// If the BROKER is unreachable, nothing answers — and reporting every device as
// offline would then be a fact about our connection, not the devices. In that
// case only, fall back to the cached status the tiles show, so the dialog and
// the badge still agree.
export async function splitByLiveness(devices) {
  const list = devices || [];
  if (!list.length) return { live: [], offline: [], method: 'mqtt' };

  const { live: answered, reachedBroker } = await probeLiveness(list.map(unoOf));

  if (!reachedBroker) {
    const marks = await mapLimit(list, STATUS_CONCURRENCY,
      (d) => getStatusCached(unoOf(d)).catch(() => UNKNOWN));
    const live = [];
    const offline = [];
    list.forEach((d, i) => (marks[i] === ONLINE ? live : offline).push(d));
    return { live, offline, method: 'status', viaStatus: live.length };
  }

  // Silence is strong evidence, but not proof: a device whose firmware predates
  // `getallwifi` would be alive and mute, and skipping it would be a NEW way to
  // report working hardware as offline — the exact fault being fixed here.
  //
  // So the ones that did not answer get a second chance against the status
  // table, where a row inside the last minute is a real sighting. Only those,
  // and only a few at a time: one request per device, all at once, is what made
  // whole locations read as dead in the first place.
  const quiet = list.filter((d) => !answered.has(String(unoOf(d))));
  const marks = await mapLimit(quiet, STATUS_CONCURRENCY,
    (d) => getStatusCached(unoOf(d)).catch(() => UNKNOWN));
  const recent = new Set();
  quiet.forEach((d, i) => { if (marks[i] === ONLINE) recent.add(String(unoOf(d))); });

  const live = [];
  const offline = [];
  list.forEach((d) => {
    const u = String(unoOf(d));
    (answered.has(u) || recent.has(u) ? live : offline).push(d);
  });
  return { live, offline, method: 'mqtt', viaStatus: recent.size };
}

// Gap between publishes. Longer than the scenes delay (350 ms) on purpose: each
// device that accepts this immediately starts an HTTP download of its .bin, so
// firing a whole location at once would have every device hit the file server
// simultaneously as well as flooding the broker.
export const UPDATE_STEP_DELAY_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Publish the update command to each device in turn.
//
// Only ever called with the LIVE list: an offline device would just drop the
// message, and it would look like it had been updated.
//
// onProgress(sentCount, total, device) fires after each publish so the UI can
// show progress — a location rollout is not instant.
export async function sendFirmwareUpdate(devices, userId, onProgress) {
  const list = devices || [];
  for (let i = 0; i < list.length; i += 1) {
    const uno = unoOf(list[i]);
    if (uno) {
      if (FIRMWARE_UPDATE_UNWRAPPED) publishRaw(uno, FIRMWARE_UPDATE_COMMAND);
      else publishCommand(uno, FIRMWARE_UPDATE_COMMAND, userId);
    }
    if (onProgress) onProgress(i + 1, list.length, list[i]);
    // eslint-disable-next-line no-await-in-loop
    if (i < list.length - 1) await sleep(UPDATE_STEP_DELAY_MS);
  }
  return list.length;
}

// Exactly what goes on the wire, for the confirmation dialog to display.
export function previewPayload(uniqueNumber, userId) {
  return FIRMWARE_UPDATE_UNWRAPPED
    ? FIRMWARE_UPDATE_COMMAND
    : `[${uniqueNumber || '<uno>'}]${FIRMWARE_UPDATE_COMMAND}#${userId}`;
}
